<template>
  <div class="main-feed pt-10 ml-5 mr-5" v-bind:style="{'zoom': '80%'}">
    <v-overlay :value="isLoading">
        <v-progress-circular 
            indeterminate color="orange"
            :size="70" :width="7"
        ></v-progress-circular>
    </v-overlay>
    <div>
        <v-row>
            <v-col class="col-6">
                <v-card>
                    <v-card-title>
                       {{title}}
                    </v-card-title>  
                    <v-card-text>                  
                        <v-select                            
                            :items="programsTypes"
                            v-model="selectedProgram"                            
                            item-text="name"
                            item-value="code"
                            label="Program"
                            ></v-select>
                        <v-alert v-if="activeProgramExecution" dense text type="info">
                            A {{ activeProgramExecution.programName }} certificate generation request is already queued or in progress.
                            <span v-if="activeProgramExecution.requestedAt">
                                Requested <localized-date-time :instant="activeProgramExecution.requestedAt" />
                                <span v-if="activeProgramExecution.requestedBy"> by {{ activeProgramExecution.requestedBy }}</span>.
                            </span>
                        </v-alert>
                        <p v-if="!!programInfo && !!programInfo.lastProcessedDate">
                            Certificates generated through <localized-date :date="programInfo.lastProcessedDate" />
                            <br/>{{programInfoSummary}}
                        </p>         
                    </v-card-text>        
                    <v-card-actions class="justify-end">
                        <v-btn 
                            color="primary" 
                            v-on:click="processShowResults"                       
                            :disabled="!canProcessShowResults"
                            >Execute
                        </v-btn>&nbsp;&nbsp;
                    </v-card-actions>    
                </v-card>
                <v-card class="mt-6">
                    <v-card-title>
                      Results
                    </v-card-title>  
                    <v-card-text>     
                       <v-alert width="100%" class="pr-2 pl-2" v-if="errorMessage" dismissible type="error"><span>{{ errorMessage }}</span></v-alert>
                       <v-data-table 
                           class="cert-generation-table"
                           :headers="certGenInfoHeaders"
                           :items="visibleCertGenInfosList"
                           :options.sync="certGenOptions"
                           :server-items-length="certGenTotalCount"
                           :loading="isCertGenListLoading"
                           disable-sort
                           dense
                           :footer-props="{ 'items-per-page-options': [25, 50, 100, 500] }"
                          fixed-header
                          height="75vh"
                          >                  
                          <template v-slot:item.progressDogsCount="{ item }">
                              <v-progress-linear
                                v-if="item.processedDogsCount != null && item.progressDogsCount != item.processedDogsCount"
                                color="orange"
                                height="50"
                                :value="(item.progressDogsCount/item.processedDogsCount)*100.0"
                              >
                              <template v-slot:default="{ value }">
                                <strong class="pl-2 pr-2">{{ item.progressDogsCount }} /{{ item.processedDogsCount }} </strong>
                              </template>                              
                            </v-progress-linear>
                            <v-progress-linear
                                v-else-if="item.processedDogsCount != null"
                                color="light-green"
                                height="25"
                                value="100"
                              >
                              {{ item.processedDogsCount }}
                            </v-progress-linear>
                            <span v-else>0</span>
                           </template>
                           <template v-slot:item.processedShowResultsEnteredAfter="{ item }">
                               <localized-date-time v-if="item.processedShowResultsEnteredAfter" :instant="item.processedShowResultsEnteredAfter" />
                           </template>
                           <template v-slot:item.certificateCounts="{ item }">
                               <v-tooltip top>
                                 <template v-slot:activator="{ on, attrs }">
                                   <span v-bind="attrs" v-on="on">
                                     {{ item.processedDogsWithNewCertsCount || 0 }}/{{ item.processedDogsWithMissingCertsCount || 0 }}/{{ item.processedDogsWithExceptionsCount || 0 }}
                                   </span>
                                 </template>
                                 <div>New certificates: {{ item.processedDogsWithNewCertsCount || 0 }}</div>
                                 <div>Deviations: {{ item.processedDogsWithMissingCertsCount || 0 }}</div>
                                 <div>Data errors: {{ item.processedDogsWithExceptionsCount || 0 }}</div>
                               </v-tooltip>
                           </template>
                          <template v-slot:item.requestedAt="{ item }">
                              <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                  <span class="date-time-tooltip" v-bind="attrs" v-on="on">
                                    <localized-date-time :instant="item.requestedAt" />
                                  </span>
                                </template>
                                <div>Requested at: {{ $dateUtils.formatInstant(item.requestedAt) }}</div>
                                <div>Requested by: {{ item.requestedBy }}</div>
                              </v-tooltip>
                          </template>
                          <template v-slot:item.processingCompletedAt="{ item }">
                              <v-tooltip v-if="item.processingCompletedAt" top>
                                <template v-slot:activator="{ on, attrs }">
                                  <span class="date-time-tooltip" v-bind="attrs" v-on="on">
                                    <localized-date-time :instant="item.processingCompletedAt" />
                                  </span>
                                </template>
                                <div>Processed at: {{ $dateUtils.formatInstant(item.processingCompletedAt) }}</div>
                                <div>Processing time: {{ $dateUtils.formatDurationHumanReadable(item.processingStartedAt, item.processingCompletedAt) }}</div>
                              </v-tooltip>
                          </template>
                          <template v-slot:item.id="{ item }">                              
                            <a v-if="item.id" v-on:click="showDogsDetails(item)">Details</a>&nbsp;&nbsp;
                           </template>
                           <template v-slot:item.canBeApproved="{ item }">
                               <v-btn
                                 v-if="item.canBeApproved"
                                 v-on:click="approve(item.id)"
                                 color="grey"
                                 icon
                                 small
                                 title="Click to approve"
                               >
                                 <v-icon>mdi-check-circle-outline</v-icon>
                               </v-btn>
                               <v-icon
                                 v-if="item.isApproved"
                                 color="green"
                                 title="Approved"
                               >
                                 mdi-check
                               </v-icon>
                           </template>
                           <template v-slot:item.canBePrinted="{ item }">
                               <v-menu v-if="item.canBePrinted" offset-y>
                                 <template v-slot:activator="{ on, attrs }">
                                   <v-btn
                                     v-bind="attrs"
                                     v-on="on"
                                     icon
                                     small
                                     title="Print options"
                                   >
                                     <v-icon>mdi-printer</v-icon>
                                   </v-btn>
                                 </template>
                                 <v-list dense>
                                   <v-list-item v-on:click="item.isPrinted ? printAgain(item.id) : preview(item.id)">
                                     <v-list-item-title>Print small</v-list-item-title>
                                   </v-list-item>
                                   <v-list-item v-on:click="item.isPrinted ? printAgainFull(item.id) : previewFull(item.id)">
                                     <v-list-item-title>Print large</v-list-item-title>
                                   </v-list-item>
                                   <v-list-item v-on:click="previewLabels(item.id)">
                                     <v-list-item-title>Print labels</v-list-item-title>
                                   </v-list-item>
                                 </v-list>
                               </v-menu>
                           </template>
                           <template v-slot:item.isPrinted="{ item }">
                               <v-btn
                                 v-if="item.canBePrinted && !item.isPrinted"
                                 v-on:click="markPrinted(item.id)"
                                 icon
                                 small
                                 title="Mark printed"
                               >
                                 <v-icon>mdi-printer-pos-play-outline</v-icon>
                                </v-btn>
                               <v-icon
                                 v-if="item.canBePrinted && item.isPrinted && !item.areNamesUpdated"
                                 class="name-update-spinner"
                                 color="orange"
                                 title="Updating names"
                               >
                                 mdi-loading
                               </v-icon>
                               <v-icon
                                 v-if="item.areNamesUpdated"
                                 color="black"
                                 title="Complete: certificates printed and names updated"
                               >
                                 mdi-certificate-outline
                               </v-icon>
                          </template>
                      </v-data-table>
                    </v-card-text>   
                </v-card>
            </v-col> 
            <v-col>
                <v-card v-if="displayDogDetails">
                    <v-card-title>
                        <v-col>
                        Dogs details
                        </v-col>
                        <v-col>
                        <filter-pills
                          :pills="filters"
                          :toggle="changeFilters"
                          class="hidden-sm-and-down"
                        />
                        </v-col>
                        <v-col>
                        <v-text-field
                          v-model="dogsDetailsSearch"
                          append-icon="mdi-magnify"
                          label="Search"
                          single-line
                          hide-details
                        ></v-text-field>
                        </v-col>
                    </v-card-title>  
                    <v-card-text>               
                       <v-data-table 
                          :search="dogsDetailsSearch"
                          :headers="certGenDogsInfoHeaders" 
                          :items="filteredCertGenDogsInfosList" 
                          dense
                          :items-per-page="-1"
                          fixed-header
                          height="95vh"
                          >    
                          <template v-slot:item.certificatesMissing="{ item }">
                            <div>
                              <span v-for="(cert,idx) in item.certificatesMissing">
                                <span>
                                  Title: {{cert.title}}
                                </span><br/>  
                                <span>
                                  Show date: <localized-date :date="cert.dateAwarded" />
                                </span>  
                                <span v-if="!!cert.dateGenerated">
                                  <br/>
                                  Generated: <localized-date :date="instantToLocalDate(cert.dateGenerated)" />
                                </span> 
                                <v-divider v-if="!!item.certificatesMissing  && idx < (item.certificatesMissing.length - 1)"></v-divider>
                              </span> 
                            </div>
                          </template>
                          <template v-slot:item.certificatesNew="{ item }">
                            <div>
                              <span v-for="(cert,idx) in item.certificatesNew">
                                <span>
                                  Title: {{cert.title}}
                                </span><br/>  
                                <span>
                                  Show date: <localized-date :date="cert.dateAwarded" />
                                </span>  
                                <span v-if="!!cert.dateGenerated">
                                  <br/>
                                  Generated: <localized-date :date="instantToLocalDate(cert.dateGenerated)" />
                                </span>
                                <v-divider v-if="!!item.certificatesNew && idx < (item.certificatesNew.length - 1)"></v-divider> 
                              </span>
                            </div>
                          </template>
                          <template v-slot:item.dogId="{ item }">
                            <a v-if="item.dogId" 
                              :href="`/admin/titling/review/${selectedGenInfo?.programName}?dogId=${encodeURIComponent(item.dogId.trim())}`"
                              target="_blank"
                              >{{item.dogId}}</a>
                          </template>
                      </v-data-table>
                    </v-card-text>   
                </v-card>
                <v-dialog v-model="displayPrintPreview">
                    <viewer
                      :reportName="Report_Name"
                      :dataUrl="Report_URL"
                    />
                </v-dialog>                
            </v-col>
        </v-row>   
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import adminTitlingService from "@/services/admin/titlingService";
import FilterPills from "@/components/Layouts/FilterPills.vue";
import Viewer from '@/components/Admin/Reports/ReportViewer.vue';
import { instantToLocalDate } from "@/helpers/date-utils";
import { getErrorMessage } from "@/services/errorHandler";
import { PaginationOptions } from "@/types/Pagination";

export default Vue.extend({
  name: "TitlingProcess",
  components: {
    FilterPills,
    Viewer
  },
  data: function () {
      return {
      title: 'Generate Certificates',
      Report_Name: 'Asca.Api.Reports.rpt8110_Title_Certificate.rdlx',
      Report_URL: '`/Admin/reports/certificates/${this.displayExecutionId}/suffix"`',    
      displayDogDetails: false,
      displayPrintPreview: false,
      displayExecutionId: null,
      isLoading: false,      
      programsTypes: [
          <object>{code: 'A', name: 'Agility'},
          <object>{code: 'R', name: 'Rally'},
          <object>{code: 'O', name: 'Obedience'},
          <object>{code: 'T', name: 'Tracking'},
          <object>{code: 'ST', name: 'Stockdog'},
          <object>{code: 'FT', name: 'Farm Trial'},
          <object>{code: 'RT', name: 'Ranch Trial'},
          <object>{code: 'C', name: 'Conformation'},
          <object>{code: 'SP', name: 'Specialty'},
          <object>{code: 'SD', name: 'Scent Detection'},
          <object>{code: 'DJ', name: 'Dock Jumping'},
        ] as any[],
      selectedProgram: '',
      programInfo: null as any,
      errorMessage: '',
      dogsDetailsSearch: '',
      certGenExecutionInfoId: null,
      startedCertGenerationExecutionId: null,
      certGenPollingTimer: null as number | null,
      hasLoadedCertGenerationList: false,
      isCertGenListLoading: false,
      certGenTotalCount: 0,
      certGenOptions: {
        page: 1,
        itemsPerPage: 25,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: false,
      } as PaginationOptions,
      certGenInfosList: [] as any[],
      certGenDogsInfosList: [] as any[],
      filteredCertGenDogsInfosList: [] as any[],
      selectedGenInfo: null,
      certGenInfoHeaders: [
        { text: "Program", align: "left", value: "programName", width: "72px" },
        { text: "Dogs processed", align: "left", value: "progressDogsCount", width: "72px", sortable: false, groupable: false },
        { text: "Results from", align: "left", value: "processedShowResultsEnteredAfter", width: "105px", groupable: false },
        { text: "New/Dev/Err", align: "left", value: "certificateCounts", width: "72px", sortable: false, groupable: false },
        { text: "", align: "left", value: "id", width: "44px", sortable: false, groupable: false },
        { text: "", align: "left", value: "canBeApproved", width: "40px", sortable: false, groupable: false },
        { text: "", align: "left", value: "canBePrinted", width: "40px", sortable: false, groupable: false },
        { text: "", align: "left", value: "isPrinted", width: "40px", sortable: false, groupable: false },
        { text: "Requested at", align: "left", value: "requestedAt", width: "105px", groupable: false },
        { text: "Processed at", align: "left", value: "processingCompletedAt", width: "105px", groupable: false },
      ],
      certGenDogsInfoHeaders: [
        { text: "Dog ID", align: "left", value: "dogId" },
        { text: "Deviations", align: "left", value: "certificatesMissing" }, 
        { text: "New", align: "left", value: "certificatesNew" }, 
        { text: "Data errors", align: "left", value: "certificatesExceptions" },    
      ],
      filters: [
        { name: "New", active: false },
        { name: "Deviations", active: false },
        { name: "Data errors", active: false },
      ],
    };
  },
  props: {
  },
  computed: {
    activeProgramExecution: function(): any | null {
      if (!this.selectedProgram) {
        return null;
      }

      return this.certGenInfosList.find(item =>
        item.program === this.selectedProgram
          && !item.processingCompletedAt
          && item.id !== this.startedCertGenerationExecutionId
      ) || null;
    },
    canProcessShowResults: function(): boolean {
      return this.hasLoadedCertGenerationList
        && !this.activeProgramExecution
        && !!this.selectedProgram
        && !!this.programInfo
        && this.programInfo.dogsCount > 0;
    },
    visibleCertGenInfosList: function(): any[] {
      return this.certGenInfosList.filter(item => item.processedDogsCount > 0);
    },
    programInfoSummary: function(): string {
      if (!this.programInfo) {
        return '';
      }

      const dogs = this.programInfo.dogsCount > 0
        ? `${this.programInfo.dogsCount} ${this.programInfo.dogsCount === 1 ? 'dog' : 'dogs'}`
        : '';
      const shows = this.programInfo.showsCount > 0
        ? `${this.programInfo.showsCount} ${this.programInfo.showsCount === 1 ? 'show' : 'shows'}`
        : '';

      if (dogs && shows) {
        return `${dogs} to process from ${shows}`;
      }

      return dogs || shows ? `${dogs || shows} to process` : 'No items to process';
    },
  },
  methods: {
    instantToLocalDate,
    preview: function (executionId: any) {   
      this.displayPrintPreview = true;
      this.displayDogDetails = false;
      this.displayExecutionId = executionId;
      this.Report_Name = "Asca.Api.Reports.rpt8110_Title_Certificate.rdlx";
      this.Report_URL = "/Admin/reports/certificates/"+this.displayExecutionId+"/suffix";
    },
    previewFull: function (executionId: any) {   
      this.displayPrintPreview = true;
      this.displayDogDetails = false;
      this.displayExecutionId = executionId;
      this.Report_Name = "Asca.Api.Reports.rpt8111_Title_Certificate_Full.rdlx";
      this.Report_URL = "/Admin/reports/certificates/"+this.displayExecutionId+"/prefix";
    },
    previewLabels: function (executionId: any) {
      this.displayPrintPreview = true;
      this.displayDogDetails = false;
      this.displayExecutionId = executionId;
      this.Report_Name = "Asca.Api.Reports.rpt1210_Person_Label.rdlx";
      this.Report_URL = "/Admin/reports/certificates/"+this.displayExecutionId+"/contacts";
    },
    printAgain: function(executionId: any){
      this.displayPrintPreview = true;
      this.displayDogDetails = false;
      this.displayExecutionId = executionId;
      this.Report_Name = "Asca.Api.Reports.rpt8110_Title_Certificate.rdlx";
      this.Report_URL = "/Admin/reports/certificates/"+this.displayExecutionId+"/suffix";
    },
    printAgainFull: function (executionId: any) {   
      this.displayPrintPreview = true;
      this.displayDogDetails = false;
      this.displayExecutionId = executionId;
      this.Report_Name = "Asca.Api.Reports.rpt8111_Title_Certificate_Full.rdlx";
      this.Report_URL = "/Admin/reports/certificates/"+this.displayExecutionId+"/prefix";
    },
    markPrinted: async function (executionId: any) {
      this.errorMessage = '';
      const execution = this.certGenInfosList.find(item => item.id === executionId);
      const previousState = execution && {
        isPrinted: execution.isPrinted,
        areNamesUpdated: execution.areNamesUpdated,
        progressUpdateNamesDogsCount: execution.progressUpdateNamesDogsCount,
      };
      if (execution) {
        execution.isPrinted = true;
        execution.areNamesUpdated = false;
        execution.progressUpdateNamesDogsCount = 0;
      }

      const restorePreviousState = () => {
        if (execution && previousState) {
          Object.assign(execution, previousState);
        }
        this.certGenExecutionInfoId = null;
        this.stopCertGenerationPolling();
      };

      try {
        const result: any = await adminTitlingService.markCertGenerationPrinted(executionId);
        if (!result.success) {
          restorePreviousState();
          this.errorMessage = getErrorMessage({ data: result });
          return;
        }

        this.certGenExecutionInfoId = executionId;
        await this.refreshCertGenerationList(true);
      } catch (error: any) {
        restorePreviousState();
        this.errorMessage = getErrorMessage(error);
      }
    },
    changeFilters: function (index: number) {
      this.filters[index].active = !this.filters[index].active;
      this.filteredCertGenDogsInfosList = [];
      if(!this.filters[0].active && !this.filters[1].active && !this.filters[2].active){
        this.filteredCertGenDogsInfosList = this.certGenDogsInfosList;
      }
      else {
        if(this.filters[0].active){
          var dogsWithNewCerts = this.certGenDogsInfosList.filter(x => !!x.certificatesNew);
          this.filteredCertGenDogsInfosList = this.filteredCertGenDogsInfosList.concat(dogsWithNewCerts);
        }
        if(this.filters[1].active){
          var dogsWithMissingCerts = this.certGenDogsInfosList.filter(x => !!x.certificatesMissing);
          this.filteredCertGenDogsInfosList = this.filteredCertGenDogsInfosList.concat(dogsWithMissingCerts);
        }
        if(this.filters[2].active){
          var dogsWithExceptions = this.certGenDogsInfosList.filter(x => !!x.certificatesExceptions);
          this.filteredCertGenDogsInfosList = this.filteredCertGenDogsInfosList.concat(dogsWithExceptions);
        }
      }
    },
    getProgramInfo: async function(program: string){
      this.programInfo = null;
      this.errorMessage = '';
      this.$store.dispatch("changeLoaderStatus",true);
      try {
        const programInfo = await adminTitlingService.getDogsWithUnprocessedResults(program);
        if (this.selectedProgram === program) {
          this.programInfo = programInfo;
        }
      } catch (error: any) {
        if (this.selectedProgram === program) {
          this.errorMessage = getErrorMessage(error);
        }
      } finally {
        this.$store.dispatch("changeLoaderStatus",false);
      }
    },
    refreshCertGenerationList: async function(pollTrackedExecution = false){
      this.isCertGenListLoading = !pollTrackedExecution;
      try {
        const trackedExecutionId = pollTrackedExecution ? this.certGenExecutionInfoId : null;
        const response = await adminTitlingService.getCertGenerationList(
          this.certGenOptions,
          trackedExecutionId ? undefined : this.selectedProgram,
          trackedExecutionId || undefined,
        );
        const refreshedExecutions: any[] = response.data;
        const pendingExecution = this.certGenInfosList.find(item => item.id === this.certGenExecutionInfoId);
        const queuedExecution = refreshedExecutions.find(item => item.id === this.certGenExecutionInfoId);
        if (pendingExecution?.processedDogsCount > 0 && queuedExecution && !queuedExecution.processingStartedAt) {
          queuedExecution.processedDogsCount = pendingExecution.processedDogsCount;
        }
        const certGenInfosList = trackedExecutionId
          ? queuedExecution
            ? this.certGenInfosList.some(item => item.id === trackedExecutionId)
              ? this.certGenInfosList.map(item => item.id === trackedExecutionId ? queuedExecution : item)
              : [queuedExecution, ...this.certGenInfosList]
            : this.certGenInfosList
          : refreshedExecutions;
        this.certGenInfosList = certGenInfosList;
        if (!trackedExecutionId) {
          this.certGenTotalCount = response.totalCount;
        }
        this.hasLoadedCertGenerationList = true;

        const execution = this.certGenExecutionInfoId
          ? certGenInfosList.find(item => item.id === this.certGenExecutionInfoId)
          : certGenInfosList.find(item => item.processedDogsCount > 0
            && (!item.processingCompletedAt || (item.isPrinted && !item.areNamesUpdated)));
        const isProcessingCertificates = execution && !execution.processingCompletedAt;
        const isUpdatingNames = execution && execution.isPrinted && !execution.areNamesUpdated;
        if ((isProcessingCertificates || isUpdatingNames) && !execution.errors) {
          this.certGenExecutionInfoId = execution.id;
          this.scheduleCertGenerationPolling();
        } else {
          this.certGenExecutionInfoId = null;
          this.stopCertGenerationPolling();
          if (trackedExecutionId && this.selectedProgram) {
            await this.getProgramInfo(this.selectedProgram);
          }
        }
      } catch (error: any) {
        if (this.certGenExecutionInfoId) {
          this.scheduleCertGenerationPolling();
        } else {
          this.errorMessage = getErrorMessage(error);
        }
      } finally {
        this.isCertGenListLoading = false;
      }
    },
    scheduleCertGenerationPolling: function(){
      this.stopCertGenerationPolling();
      this.certGenPollingTimer = window.setTimeout(() => this.refreshCertGenerationList(true), 1000);
    },
    stopCertGenerationPolling: function(){
      if (this.certGenPollingTimer !== null) {
        window.clearTimeout(this.certGenPollingTimer);
        this.certGenPollingTimer = null;
      }
    },
    processShowResults: async function(){
      if (!this.canProcessShowResults) {
        return;
      }

      const program = this.selectedProgram;
      const pendingExecution: any = {
        id: null,
        program,
        programName: this.programsTypes.find(item => item.code === program)?.name || program,
        processedDogsCount: this.programInfo.dogsCount,
        progressDogsCount: 0,
        requestedAt: Temporal.Now.instant(),
      };

      this.stopCertGenerationPolling();
      this.certGenExecutionInfoId = null;
      this.startedCertGenerationExecutionId = null;
      this.certGenInfosList = [pendingExecution, ...this.certGenInfosList];
      this.programInfo = null;

      try {
        const result: any = await adminTitlingService.processShowResults(program);
        this.certGenExecutionInfoId = result.certGenExecutionInfoId;
        this.startedCertGenerationExecutionId = result.certGenExecutionInfoId;
        pendingExecution.id = result.certGenExecutionInfoId;
        await this.refreshCertGenerationList(true);
      } catch (error: any) {
        this.certGenInfosList = this.certGenInfosList.filter(item => item !== pendingExecution);
        this.errorMessage = getErrorMessage(error);
      }
    },
    approve: async function(id: number){
      this.errorMessage = '';
      this.$store.dispatch("changeLoaderStatus",true);
      try {
        const result: any = await adminTitlingService.approveShowResults(id);
        if (!result.success) {
          this.errorMessage = getErrorMessage({ data: result });
          return;
        }

        await this.refreshCertGenerationList();
      } catch (error: any) {
        this.errorMessage = getErrorMessage(error);
      } finally {
        this.$store.dispatch("changeLoaderStatus",false);
      }
    },
    showDogsDetails(certGenExecutionInfo: any){        
        this.displayDogDetails = true;     
        this.displayPrintPreview = false;
        this.selectedGenInfo = certGenExecutionInfo;
        adminTitlingService.getCertGenerationDogsList(certGenExecutionInfo.id)
              .then((res: any) => {
                this.certGenDogsInfosList = res;
                this.filteredCertGenDogsInfosList = res;
              });
    },
  },
  beforeDestroy() {
    this.stopCertGenerationPolling();
  },
  watch: {
    selectedProgram: function(program: string){
      this.programInfo = null;
      this.displayDogDetails = false;
      this.displayPrintPreview = false;
      if (program) {
        this.getProgramInfo(program);
      }
      if (this.certGenOptions.page === 1) {
        this.refreshCertGenerationList();
      } else {
        this.certGenOptions.page = 1;
      }
    },
    certGenOptions: {
      handler() {
        this.refreshCertGenerationList();
      },
      deep: true,
    },
  },
});
</script>

<style scoped>
::v-deep .cert-generation-table thead th {
  vertical-align: middle;
  white-space: nowrap;
}

::v-deep .cert-generation-table tbody td {
  vertical-align: middle;
}

::v-deep .cert-generation-table :is(th, td):nth-child(n + 4):nth-child(-n + 8) {
  padding-left: 4px;
  padding-right: 4px;
}

::v-deep .cert-generation-table :is(th, td):nth-child(3),
::v-deep .cert-generation-table :is(th, td):nth-child(9),
::v-deep .cert-generation-table :is(th, td):nth-child(10) {
  max-width: 105px;
  min-width: 105px;
  width: 105px;
}

::v-deep .cert-generation-table :is(th:nth-child(2), th:nth-child(3), th:nth-child(9), th:nth-child(10)) {
  white-space: normal;
}

::v-deep .cert-generation-table :is(td:nth-child(3), td:nth-child(9), td:nth-child(10)) time {
  white-space: normal;
}

::v-deep .date-time-tooltip time {
  pointer-events: none;
}

.name-update-spinner {
  animation: name-update-spin 0.8s linear infinite;
}

@keyframes name-update-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
